import {
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  IconButton,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { SelectColor } from './SelectColor';
import { Icons } from '@texas/components/shared/Icons';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { SpacerTextAlignment, SpacerWidgetOptions, WidgetType } from '../types';
import { AutoGrowTextareaContainer } from '@texas/components/shared/form/AutoGrowTextareaContainer';

export const DEFAULT_SPACER_FONT_SIZE = 14;
const FONT_SIZE_STEPS = [14, 18, 24, 30] as const;
type SpacerFontSize = typeof FONT_SIZE_STEPS[number];

export interface SpacerWidgetSettingsHandle {
  getValues(): SpacerWidgetOptions;
}
interface SpacerWidgetSettingsProps {
  initialOptions?: SpacerWidgetOptions;
  namePlaceholder?: string;
}

export const SpacerWidgetSettings = forwardRef<
  SpacerWidgetSettingsHandle,
  SpacerWidgetSettingsProps
>(function SpacerWidgetSettings({ initialOptions, namePlaceholder }, ref) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialOptions?.customWidgetTitle ?? '');
  const [alignment, setAlignment] = useState<SpacerTextAlignment>(
    initialOptions?.alignment ?? 'center',
  );
  const [fontSize, setFontSize] = useState<SpacerFontSize>(
    FONT_SIZE_STEPS.includes(initialOptions?.fontSize as SpacerFontSize)
      ? (initialOptions!.fontSize as SpacerFontSize)
      : DEFAULT_SPACER_FONT_SIZE,
  );
  const [textColor, setTextColor] = useState<string | null>(
    initialOptions?.textColor ?? null,
  );

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      customWidgetTitle: name,
      type: WidgetType.Spacer as const,
      alignment,
      fontSize,
      textColor,
    }),
  }));

  return (
    <Flex flexDir="column" gap={4}>
      <TexasInputWrapper label={t('widgets.name')}>
        <AutoGrowTextareaContainer value={name}>
          <Textarea
            value={name}
            placeholder={namePlaceholder ?? t('widgets.spacer.title')}
            onChange={(e) => setName(e.target.value)}
            height="auto"
            variant="unstyled"
            padding={1}
            width="100%"
          />
        </AutoGrowTextareaContainer>
      </TexasInputWrapper>

      <FormControl>
        <TexasFormLabel pb={1}>{t('widgets.spacer.alignment')}</TexasFormLabel>
        <ButtonGroup isAttached={true} variant="outline" size="sm">
          <Tooltip label={t('widgets.spacer.alignLeft')}>
            <IconButton
              aria-label={t('widgets.spacer.alignLeft')}
              icon={<Icons.FormatAlignLeft boxSize={4} />}
              onClick={() => setAlignment('left')}
              colorScheme={alignment === 'left' ? 'texasBrand' : 'gray'}
              variant={alignment === 'left' ? 'solid' : 'outline'}
            />
          </Tooltip>
          <Tooltip label={t('widgets.spacer.alignCenter')}>
            <IconButton
              aria-label={t('widgets.spacer.alignCenter')}
              icon={<Icons.FormatAlignCenter boxSize={4} />}
              onClick={() => setAlignment('center')}
              colorScheme={alignment === 'center' ? 'texasBrand' : 'gray'}
              variant={alignment === 'center' ? 'solid' : 'outline'}
            />
          </Tooltip>
          <Tooltip label={t('widgets.spacer.alignRight')}>
            <IconButton
              aria-label={t('widgets.spacer.alignRight')}
              icon={<Icons.FormatAlignRight boxSize={4} />}
              onClick={() => setAlignment('right')}
              colorScheme={alignment === 'right' ? 'texasBrand' : 'gray'}
              variant={alignment === 'right' ? 'solid' : 'outline'}
            />
          </Tooltip>
        </ButtonGroup>
      </FormControl>

      <FormControl>
        <TexasFormLabel pb={1}>{t('widgets.spacer.fontSize')}</TexasFormLabel>
        <ButtonGroup isAttached={true} variant="outline" size="sm">
          {FONT_SIZE_STEPS.map((size) => (
            <Button
              key={size}
              onClick={() => setFontSize(size)}
              colorScheme={fontSize === size ? 'texasBrand' : 'gray'}
              variant={fontSize === size ? 'solid' : 'outline'}
            >
              {size}px
            </Button>
          ))}
        </ButtonGroup>
      </FormControl>

      <FormControl>
        <TexasFormLabel pb={1}>{t('widgets.spacer.textColor')}</TexasFormLabel>
        <SelectColor selectedColor={textColor} onChange={setTextColor} />
      </FormControl>
    </Flex>
  );
});
